// utils/fai-calculator.ts

export interface LatLng {
  lat: number;
  lng: number;
}

export interface RouteScore {
  type: 'fai_triangle' | 'flat_triangle' | 'free_distance' | 'invalid';
  distance: number;
  gap?: number;
  legs?: number[];
  points?: LatLng[];
  valid: boolean;
}

// Haversine distance in km
export function getDistance(p1: LatLng, p2: LatLng): number {
  const R = 6371; // Earth radius in km
  const dLat = (p2.lat - p1.lat) * Math.PI / 180;
  const dLon = (p2.lng - p1.lng) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Calculate the total length of an array of legs
export function getPathLength(points: LatLng[]): number {
  let length = 0;
  for (let i = 0; i < points.length - 1; i++) {
    length += getDistance(points[i], points[i + 1]);
  }
  return length;
}

/**
 * Evaluates a planned route for its XContest score equivalent.
 * Rules based on typical Paragliding XC standards:
 * - Triangle: max 3 turnpoints (Start, TP1, TP2, End). Distance = perimeter.
 * - Gap: distance between Start and End must be < 20% of perimeter to be closed.
 * - FAI: shortest leg of the triangle must be >= 28% of perimeter.
 * - Flat: shortest leg < 28% of perimeter.
 * - Free distance: up to 3 turnpoints (4 legs max). Distance = sum of legs.
 */
export function evaluateRoute(points: LatLng[]): RouteScore {
  if (!points || points.length < 2) {
    return { type: 'invalid', distance: 0, valid: false };
  }

  const totalDist = getPathLength(points);
  const start = points[0];
  const end = points[points.length - 1];
  const gap = getDistance(start, end);
  
  // XContest rule: gap must be <= 20% of the planned triangle perimeter.
  const isClosed = gap <= (totalDist + gap) * 0.20;

  if (!isClosed) {
    return {
      type: 'free_distance',
      distance: totalDist,
      valid: true
    };
  }

  // Helper zum Überprüfen auf Dreieck
  const checkTriangle = (v1: LatLng, v2: LatLng, v3: LatLng) => {
    const leg1 = getDistance(v1, v2);
    const leg2 = getDistance(v2, v3);
    const leg3 = getDistance(v3, v1); // Kante zurück zum ersten Eckpunkt
    
    const perimeter = leg1 + leg2 + leg3;

    if (perimeter > 0) {
      const triangleDist = perimeter;
      const minLeg = Math.min(leg1, leg2, leg3);

      if (minLeg >= perimeter * 0.28) {
        return {
          type: 'fai_triangle' as const,
          distance: triangleDist,
          gap: gap,
          legs: [leg1, leg2, leg3],
          points: [v1, v2, v3],
          valid: true
        };
      } else {
        return {
          type: 'flat_triangle' as const,
          distance: triangleDist,
          gap: gap,
          legs: [leg1, leg2, leg3],
          points: [v1, v2, v3],
          valid: true
        };
      }
    }
    return null;
  };

  let bestTriangle: RouteScore | null = null;
  
  if (points.length >= 3) {
    // Bilde alle 3er Kombinationen aus den Punkten
    for (let i = 0; i < points.length - 2; i++) {
      for (let j = i + 1; j < points.length - 1; j++) {
        for (let k = j + 1; k < points.length; k++) {
          const triScore = checkTriangle(points[i], points[j], points[k]);
          if (triScore) {
            if (!bestTriangle || triScore.distance > bestTriangle.distance) {
              bestTriangle = triScore;
            }
          }
        }
      }
    }
  }
  
  if (bestTriangle) {
    return bestTriangle;
  }
  
  // Wenn kein geschlossenes Dreieck, ist es eine freie Strecke
  return {
    type: 'free_distance',
    distance: totalDist,
    valid: true
  };
}

export interface MacCreadyParams {
  v_travel?: number; // average travel speed in km/h (15..45, default 25)
  macCready?: number; // MacCready value in m/s (default 0.0)
  s_trim?: number; // trim sink rate in m/s (default -1.1)
}

export interface MacCreadyResult {
  v_base: number; // base speed in m/s (v_travel / 3.6)
  deltaV: number; // 1.5 * sqrt(MC) in m/s
  v_air: number; // v_base + deltaV in m/s
  s_glide: number; // s_trim - 0.08 * deltaV^2 in m/s
}

/**
 * Calculates MacCready air speed and glide sink rate.
 * v_base = v_travel / 3.6 (in m/s)
 * Delta v = 1.5 * sqrt(MC) (in m/s)
 * v_air = v_base + Delta v (in m/s)
 * s_glide = s_trim - 0.08 * Delta v^2 (in m/s, default s_trim = -1.1 m/s)
 */
export function calculateMacCreadySpeed(
  v_travel: number = 25,
  macCready: number = 0,
  s_trim: number = -1.1
): MacCreadyResult {
  const travelKmh = Math.max(15, Math.min(70, Number(v_travel) || 25));
  const v_base = travelKmh / 3.6;
  const deltaV = 1.5 * Math.sqrt(Math.max(0, Number(macCready) || 0));
  const v_air = v_base + deltaV;
  const s_trim_ms = Number(s_trim) !== 0 ? Number(s_trim) : -1.1;
  const s_glide = s_trim_ms - 0.08 * Math.pow(deltaV, 2);
  return { v_base, deltaV, v_air, s_glide };
}

